import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, Loader2, Ticket, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DahliasShell from "../components/dahlias/DahliasShell";
import customerApi, { API } from "../hq/customerApi";

const nz = (n) => `NZ$${Number(n || 0).toFixed(2)}`;

export function TicketStub({ o }) {
  return (
    <div className="rounded-[28px] border-[3px] border-[var(--berry)] bg-white overflow-hidden" data-testid={`ticket-stub-${o.ref}`}>
      <div className="bg-[var(--berry)] text-white px-6 py-4">
        <div className="font-label text-[9px] tracking-[0.4em] uppercase opacity-80">Heritage Open Day</div>
        <div className="font-display text-2xl mt-1">{o.event_title}</div>
        <div className="mt-2 flex flex-wrap gap-4 text-xs opacity-90">
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {o.event_date || "Date to be confirmed"}</span>
          {o.event_times && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {o.event_times}</span>}
        </div>
      </div>
      <div className="px-6 py-5 grid gap-5 sm:grid-cols-[1fr_auto] items-center">
        <div>
          <div className="text-sm text-[var(--dahlias-ink)]/70">{o.customer_name}</div>
          <ul className="mt-2 text-sm text-[var(--dahlias-ink)]">
            {o.lines.map((ln, i) => <li key={i}>{ln.qty} × {ln.name} <span className="text-[var(--dahlias-ink)]/50">{ln.price > 0 ? nz(ln.price * ln.qty) : "free"}</span></li>)}
          </ul>
          <div className="mt-2 text-xs text-[var(--dahlias-ink)]/60">{o.ref} · {o.qty_total} guest{o.qty_total === 1 ? "" : "s"} · {o.total > 0 ? `${nz(o.total)} paid` : "complimentary"}</div>
        </div>
        <div className="rounded-2xl border-2 border-dashed border-[var(--berry)]/50 px-6 py-4 text-center">
          <div className="font-label text-[9px] tracking-[0.35em] uppercase text-[var(--berry)]">Gate code</div>
          <div className="font-display text-3xl tracking-[0.25em] text-[var(--dahlias-ink)] mt-1" data-testid={`ticket-code-${o.ref}`}>{o.code}</div>
        </div>
      </div>
    </div>
  );
}

export default function OpenDayTicketPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [o, setO] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const [busy, setBusy] = useState(false);

  const load = () => customerApi.get(`/customer/open-day-tickets/${orderId}`).then((r) => setO(r.data)).catch(() => setO(false));

  useEffect(() => {
    let tries = 0, timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid" || r.data.status === "expired" || tries >= 12) { setPolling(false); return load(); }
      } catch { if (tries >= 12) { setPolling(false); return load(); } }
      tries += 1; timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else load();
    return () => clearTimeout(timer);
  }, [orderId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const payAgain = async () => {
    setBusy(true);
    try {
      const r = await customerApi.post(`/customer/open-day-tickets/${orderId}/pay`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't restart payment."); setBusy(false); }
  };

  return (
    <DahliasShell>
      <section className="bg-[var(--dahlias-cream)] min-h-[60vh]" data-testid="open-day-ticket-page">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12">
          <Link to="/account/tickets" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[var(--berry)]"><ArrowLeft className="w-3.5 h-3.5" /> My Tickets</Link>
          {(polling || o === null) && <div className="mt-10 flex items-center gap-3 text-[var(--dahlias-ink)]/70" data-testid="ticket-polling"><Loader2 className="w-5 h-5 animate-spin text-[var(--berry)]" /> {polling ? "Confirming your payment with Stripe…" : "Loading your ticket…"}</div>}
          {o === false && <p className="mt-8 text-[var(--dahlias-ink)]/70">We couldn't find that ticket on your account. <Link to="/account/login" className="underline">Sign in</Link> and try again.</p>}
          {o && !polling && (o.status === "paid" || o.status === "comp") && (
            <>
              <div className="mt-8 inline-flex items-center gap-2 text-[#2F6B4F] font-semibold" data-testid="ticket-confirmed"><CheckCircle2 className="w-5 h-5" /> You're booked in!</div>
              <h1 className="font-display text-4xl sm:text-5xl text-[var(--dahlias-ink)] mt-2">See you at the farm</h1>
              <p className="text-sm text-[var(--dahlias-ink)]/70 mt-2">We've emailed this ticket to {o.customer_email}. Show the gate code on arrival , on your phone is perfect.</p>
              <div className="mt-6"><TicketStub o={o} /></div>
            </>
          )}
          {o && !polling && (o.status === "pending" || o.status === "expired") && (
            <div className="mt-8 rounded-[24px] border-[3px] border-[#C9A268] bg-white p-6" data-testid="ticket-unpaid">
              <div className="inline-flex items-center gap-2 text-[#8B4A0B] font-semibold"><AlertTriangle className="w-5 h-5" /> {cancelled ? "Payment cancelled" : "Payment not completed yet"}</div>
              <p className="text-sm text-[var(--dahlias-ink)]/75 mt-2">{o.status === "pending" ? `Your ${o.qty_total} ticket${o.qty_total === 1 ? " is" : "s are"} held until ${new Date(o.expires_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}. Finish paying to lock them in.` : "Your hold has lapsed , if there's still room we'll hold the tickets again while you pay."}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" onClick={payAgain} disabled={busy} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--berry)] text-white font-label text-xs tracking-[0.3em] uppercase disabled:opacity-40" data-testid="ticket-pay-again">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Pay {nz(o.total)} now</button>
                <Link to={`/visit/open-days/${o.event_slug}`} className="px-6 py-3 rounded-full border-2 border-[var(--berry)]/40 text-[var(--berry)] font-label text-xs tracking-[0.3em] uppercase">Back to event</Link>
              </div>
            </div>
          )}
          {o && !polling && (o.status === "cancelled" || o.status === "refunded") && (
            <div className="mt-8 rounded-[24px] border-[3px] border-[#A33030]/40 bg-white p-6" data-testid="ticket-cancelled">
              <div className="font-display text-2xl text-[#A33030]">This ticket was {o.status}</div>
              <p className="text-sm text-[var(--dahlias-ink)]/70 mt-1">{o.ref} · {o.event_title}. Questions? Message Heritage from HQ.</p>
            </div>
          )}
        </div>
      </section>
    </DahliasShell>
  );
}
